import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CartModal from '../components/user/CartModal';
import LoginModal from '../components/user/LoginModal';
import RegisterModal from '../components/user/RegisterModal';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, getImageUrl } from '../utils/config';
import { formatPrice } from '../utils/currency';

const StoreHomePage = () => {
  const { slug } = useParams();
  const { isAdmin } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [screenSize, setScreenSize] = useState('mobile');
  const [productQuantities, setProductQuantities] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchStore();
    checkAuthStatus();
  }, [slug]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop'); // lg and above
      } else if (width >= 768) {
        setScreenSize('tablet'); // md
      } else {
        setScreenSize('mobile'); // sm and below
      }
    };

    handleResize(); // Set initial size
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (store) {
      // Redirect if store has redirect_url and user is not admin
      if (store.redirect_url && !isAdmin) {
        window.location.href = store.redirect_url;
        return;
      }
      // Initialize quantities for all products
      const quantities = {};
      store.products?.forEach(product => {
        quantities[product.id] = 1;
      });
      setProductQuantities(quantities);
    }
    // eslint-disable-next-line
  }, [store, isAdmin]);

  const fetchStore = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl(`/stores/${slug}`));
      setStore(response.data.data);
    } catch (error) {
      console.error('Error fetching store:', error);
      setError('Failed to load store');
    } finally {
      setLoading(false);
    }
  };

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  };

  // تحديث handleAddToCart ليحدث البادج فورًا
  const handleAddToCart = async (product) => {
    const quantity = productQuantities[product.id] || 1;
    
    // Set loading state for this specific product
    setLoadingStates(prev => ({ ...prev, [`add_${product.id}`]: true }));
    
    if (!isLoggedIn) {
      const localCartKey = `cart_${store.id}`;
      const existingCart = JSON.parse(localStorage.getItem(localCartKey) || '[]');

      // Check if product already exists in cart
      const existingItemIndex = existingCart.findIndex(item => item.id === product.id);

      if (existingItemIndex >= 0) {
        // Update quantity if item exists
        existingCart[existingItemIndex].quantity += quantity;
      } else {
        // Add new item to cart
        existingCart.push({
          id: product.id,
          name: product.name,
          price: product.price,
          discount_percentage: product.discount_percentage,
          image: product.image,
          quantity: quantity
        });
      }

      // Save updated cart to localStorage
      localStorage.setItem(localCartKey, JSON.stringify(existingCart));

      // Show success message
      toast.success(`${quantity} product(s) added to cart successfully!`, {
        autoClose: 1000,
      });
      setLoadingStates(prev => ({ ...prev, [`add_${product.id}`]: false }));
      return;
    }

    // If logged in, add to backend cart
    try {
      const token = localStorage.getItem('token');
      await axios.post(getApiUrl(`/cart/${store.id}/add`), {
        product_id: product.id,
        quantity: quantity
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Show success message
      toast.success(`${quantity} product(s) added to cart successfully!`, {
        autoClose: 1000,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add product to cart', {
        autoClose: 1000,
      });
    }
    
    // Clear loading state
    setLoadingStates(prev => ({ ...prev, [`add_${product.id}`]: false }));
  };



  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    // Set loading state for this specific product
    setLoadingStates(prev => ({ ...prev, [`quantity_${productId}`]: true }));
    
    // Simulate a small delay for better UX
    setTimeout(() => {
      setProductQuantities(prev => ({
        ...prev,
        [productId]: newQuantity
      }));
      setLoadingStates(prev => ({ ...prev, [`quantity_${productId}`]: false }));
    }, 200);
  };

  const handleCartUpdate = () => {
    // Cart update handled by CartModal
  };

  const filteredCategories = store?.categories?.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const filteredProducts = store?.products?.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getProductsByCategory = (categoryId) => {
    return store?.products?.filter(product => product.category_id === categoryId) || [];
  };

  const getProductsToShow = (products) => {
    switch (screenSize) {
      case 'desktop':
        return products.slice(0, 4); // 4 products for desktop
      case 'tablet':
        return products.slice(0, 3); // 3 products for tablet
      default:
        return products.slice(0, 2); // 2 products for mobile
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading store...</p>
        </div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Store not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // If store has redirect_url and user is not admin, stay in loading
  if (store.redirect_url && !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
              {store.icon ? (
                <img
                  src={getImageUrl(store.icon)}
                  alt={store.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              )}
            </div>
            <h1 className="text-xl font-semibold text-gray-900">{store.name}</h1>
          </div>

          {/* Phone Number */}
          {store.phone && (
            <div className="flex items-center space-x-2 text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-sm font-medium">{store.phone}</span>
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Search Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search categories and products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
            />
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Categories Section */}
        {filteredCategories.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Categories</h2>
            <div className="grid grid-cols-3 lg:grid-cols-6 gap-4">
              {filteredCategories.map((category) => (
                <Link
                  key={category.id}
                  to={`/stores/${slug}/categories/${category.id}`}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center hover:shadow-md transition-shadow group"
                >
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                    {category.icon ? (
                      <img
                        src={getImageUrl(category.icon)}
                        alt={category.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 group-hover:text-emerald-600 transition-colors">
                    {category.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Products by Category */}
        {filteredCategories.length > 0 && (
          <div className="space-y-8">
            {filteredCategories.map((category) => {
              // Get all products for this category and show based on screen size
              const allCategoryProducts = getProductsByCategory(category.id);
              const categoryProducts = getProductsToShow(allCategoryProducts);
              if (categoryProducts.length === 0) return null;

              return (
                <div key={category.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                    <Link
                      to={`/stores/${slug}/categories/${category.id}`}
                      className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                    >
                      View All
                    </Link>
                  </div>
                  <div className="border-b border-gray-200 mb-4"></div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {categoryProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        storeSlug={slug}
                        quantity={productQuantities[product.id] || 1}
                        onQuantityChange={handleQuantityChange}
                        onAddToCart={() => handleAddToCart(product)}
                        loadingStates={loadingStates}
                        store={store}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* All Products Section (if no categories or search) */}
        {filteredCategories.length === 0 && filteredProducts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">All Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  storeSlug={slug}
                  quantity={productQuantities[product.id] || 1}
                  onQuantityChange={handleQuantityChange}
                  onAddToCart={() => handleAddToCart(product)}
                  loadingStates={loadingStates}
                  store={store}
                />
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {filteredCategories.length === 0 && filteredProducts.length === 0 && searchTerm && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-500">Try adjusting your search terms</p>
          </div>
        )}
      </div>

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-around h-16">
            <Link
              to={`/stores/${slug}`}
              className="flex flex-col items-center space-y-1 text-emerald-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="text-xs font-medium">Home</span>
            </Link>

            <Link
              to={`/stores/${slug}/about`}
              className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs font-medium">About</span>
            </Link>

            {/* Cart Button */}
            <button
              onClick={() => setShowCart(true)}
              className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900 relative"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
              </svg>
              <span className="text-xs font-medium">Cart</span>

            </button>

            {/* Profile/Login Button */}
            {isLoggedIn ? (
              <Link
                to={`/stores/${slug}/account`}
                className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-xs font-medium">Account</span>
              </Link>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-xs font-medium">Account</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Modals */}
      {showCart && (
        <CartModal
          storeId={store.id}
          onClose={() => setShowCart(false)}
          onCartUpdate={handleCartUpdate}
          store={store}
        />
      )}

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onRegisterClick={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
        />
      )}

      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onLoginClick={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}
      </div>
  );
};

// Product Card Component
const ProductCard = ({ product, storeSlug, onAddToCart, quantity, onQuantityChange, loadingStates = {}, store }) => {
  const calculateDiscountedPrice = () => {
    const price = parseFloat(product.price) || 0;
    const discount = parseFloat(product.discount_percentage) || 0;

    if (discount > 0) {
      return price - (price * discount / 100);
    }
    return price;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group">
      <Link to={`/products/${product.id}`}>
        <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
          {product.image ? (
            <img
              src={getImageUrl(product.image)}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
          ) : (
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          )}
        </div>
      </Link>

      <div className="p-3">
        <Link to={`/products/${product.id}`}>
          <h3 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
            {product.name}
          </h3>
        </Link>

        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            {parseFloat(product.discount_percentage) > 0 ? (
              <>
                <span className="text-sm font-bold text-green-600">
                  {formatPrice(calculateDiscountedPrice(), store?.currency)}
                </span>
                <span className="text-xs text-gray-500 line-through">
                  {formatPrice(product.price, store?.currency)}
                </span>
              </>
            ) : (
              <span className="text-sm font-bold text-gray-900">
                {formatPrice(product.price, store?.currency)}
              </span>
            )}
          </div>
        </div>

        {/* Quantity Controls */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onQuantityChange(product.id, quantity - 1)}
              disabled={quantity <= 1 || loadingStates[`quantity_${product.id}`]}
              className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingStates[`quantity_${product.id}`] ? (
                <div className="animate-spin rounded-full h-2 w-2 border-b border-gray-600"></div>
              ) : (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              )}
            </button>
            <span className="text-sm font-medium w-6 text-center">{quantity}</span>
            <button
              onClick={() => onQuantityChange(product.id, quantity + 1)}
              disabled={loadingStates[`quantity_${product.id}`]}
              className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingStates[`quantity_${product.id}`] ? (
                <div className="animate-spin rounded-full h-2 w-2 border-b border-gray-600"></div>
              ) : (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={onAddToCart}
          disabled={loadingStates[`add_${product.id}`]}
          className="w-full bg-emerald-600 text-white py-2 px-3 rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loadingStates[`add_${product.id}`] ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b border-white mr-2"></div>
              Adding...
            </>
          ) : (
            'Add to Cart'
          )}
        </button>
      </div>
    </div>
  );
};

export default StoreHomePage; 